import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "~/components/ui/toaster";
import { createMap } from "../lib/api";
import { routes } from "../lib/routes";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Icon } from "../components/ui/icons/icon";
import { MapForm } from "../components/builder";

/**
 * Create new map view - simple form overlay.
 * Rendered on the shared map (header elements in parent BuilderLayout).
 */
export default function MapCreate() {
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      setError("Map name is required");
      return;
    }

    setSaving(true);
    setError("");

    try {
      const newMap = await createMap({
        name: name.trim(),
        description: description.trim() || undefined,
      });
      toast.success("Map created successfully");
      navigate(routes.to.maps.view(newMap.id), { replace: true });
    } catch (err) {
      const error = err as {
        response?: { data?: { error?: { message?: string } } };
      };
      setError(error.response?.data?.error?.message || "Failed to create map");
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => navigate(routes.to.maps.root());

  return (
    <Dialog open onOpenChange={(open) => !open && handleClose()}>
      <DialogContent showCloseButton={false} className="md:max-w-lg">
        <DialogHeader className="flex-row items-center gap-2">
          <Button asChild variant="ghost" size="icon-sm" aria-label="Back to maps">
            <Link to={routes.to.maps.root()}>
              <Icon i="back" className="size-5" />
            </Link>
          </Button>
          <DialogTitle className="text-lg md:text-2xl md:font-semibold">Create New Map</DialogTitle>
        </DialogHeader>
        <MapForm
          name={name}
          description={description}
          onNameChange={setName}
          onDescriptionChange={setDescription}
          onSubmit={handleSubmit}
          saving={saving}
          error={error}
        />
      </DialogContent>
    </Dialog>
  );
}
